import React, { useState, useEffect, useCallback } from 'react';
import '../Timbra.css';
import api from '../api';
import { LogIn, LogOut, MapPin, Clock, CheckCircle2, XCircle, AlertTriangle, Lock, Mail } from 'lucide-react';

const LOGO = 'https://customer-assets.emergentagent.com/job_colabella-tutorials/artifacts/wcwuz41r_LOGO.png';

const Timbra = () => {
  const [user, setUser] = useState(null);
  const [worker, setWorker] = useState(null);
  const [cantiere, setCantiere] = useState(null);
  const [location, setLocation] = useState(null);
  const [geoStatus, setGeoStatus] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPwd, setLoginPwd] = useState('');
  const [loginError, setLoginError] = useState('');
  const [lastAction, setLastAction] = useState(null);

  // Clock
  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // GPS
  useEffect(() => {
    if (navigator.geolocation) {
      const watchId = navigator.geolocation.watchPosition(
        pos => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setLocation(null),
        { enableHighAccuracy: true, maximumAge: 30000 }
      );
      return () => navigator.geolocation.clearWatch(watchId);
    }
  }, []);

  // Load worker data after login
  const loadWorker = useCallback(async () => {
    if (!user) return;
    try {
      const operai = await api.getOperai();
      const me = operai.find(o => o.name.toLowerCase() === user.name?.toLowerCase());
      if (me) {
        setWorker(me);
        const cantieri = await api.getCantieri();
        const myCantiere = cantieri.find(c => c.id === me.cantiereId);
        setCantiere(myCantiere || null);
      }
    } catch {}
  }, [user]);

  useEffect(() => { loadWorker(); }, [loadWorker]);

  // Check geofence
  useEffect(() => {
    if (!worker || !cantiere || !location) return;
    if (!cantiere.latitude || cantiere.latitude === 0) { setGeoStatus({ inside: true, message: 'GPS cantiere non impostato' }); return; }
    const R = 6371000;
    const dLat = (cantiere.latitude - location.lat) * Math.PI / 180;
    const dLon = (cantiere.longitude - location.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2)**2 + Math.cos(location.lat*Math.PI/180)*Math.cos(cantiere.latitude*Math.PI/180)*Math.sin(dLon/2)**2;
    const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const radius = cantiere.geofence_radius || 200;
    setGeoStatus({ inside: dist <= radius, distance: Math.round(dist), radius, message: dist <= radius ? `Sei nel cantiere (${Math.round(dist)}m)` : `Sei a ${Math.round(dist)}m — Torna entro ${radius}m!` });
  }, [worker, cantiere, location]);

  // Check URL for cantiere QR
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cId = params.get('c');
    if (cId && user) {
      api.getCantieri().then(cantieri => {
        const c = cantieri.find(x => x.id === cId);
        if (c) setCantiere(c);
      });
    }
  }, [user]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const data = await api.loginPassword(loginEmail, loginPwd);
      if (data.session_token) {
        localStorage.setItem('colabella_auth', JSON.stringify(data));
        setUser(data);
      }
    } catch { setLoginError('Email o password errati'); }
  };

  const handleTimbratura = async (type) => {
    if (!worker) return;
    setLoading(true);
    try {
      await api.createTimbratura({
        workerId: worker.id, type,
        lat: location?.lat || 0, lng: location?.lng || 0
      });
      setLastAction({ type, time: new Date() });
      setTimeout(() => setLastAction(null), 5000);
      loadWorker();
    } catch (e) {
      alert(e.response?.data?.detail || 'Errore nella timbratura');
    }
    setLoading(false);
  };

  // Check stored auth
  useEffect(() => {
    try {
      const stored = localStorage.getItem('colabella_auth');
      if (stored) setUser(JSON.parse(stored));
    } catch {}
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('colabella_auth');
    setUser(null); setWorker(null); setCantiere(null);
  };

  // LOGIN SCREEN
  if (!user) return (
    <div className="tmb-page">
      <div className="tmb-login">
        <img src={LOGO} alt="CG" className="tmb-logo" />
        <h1 className="tmb-brand">COLABELLA GROUP</h1>
        <p className="tmb-sub">Timbratura Cantiere</p>
        <form onSubmit={handleLogin} className="tmb-form">
          {loginError && <div className="tmb-error">{loginError}</div>}
          <div className="tmb-field"><Mail size={18} /><input type="email" placeholder="Email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} /></div>
          <div className="tmb-field"><Lock size={18} /><input type="password" placeholder="Password" value={loginPwd} onChange={e => setLoginPwd(e.target.value)} /></div>
          <button type="submit" className="tmb-login-btn">Accedi</button>
        </form>
      </div>
    </div>
  );

  // TIMBRATURA SCREEN
  return (
    <div className="tmb-page">
      <div className="tmb-main">
        {/* Header */}
        <div className="tmb-header">
          <img src={LOGO} alt="" className="tmb-header-logo" />
          <div className="tmb-header-info">
            <h2 className="tmb-worker-name">{user.name}</h2>
            {worker && <p className="tmb-worker-role">{worker.role} — {worker.badge}</p>}
            {cantiere && <p className="tmb-worker-cantiere"><MapPin size={12} /> {cantiere.name}</p>}
          </div>
          <button className="tmb-logout" onClick={handleLogout}>Esci</button>
        </div>

        {/* Clock */}
        <div className="tmb-clock">
          <span className="tmb-time">{currentTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
          <span className="tmb-date">{currentTime.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
        </div>

        {/* GPS Status */}
        <div className={`tmb-geo ${geoStatus?.inside ? 'tmb-geo-ok' : 'tmb-geo-out'}`}>
          {!location ? (
            <><AlertTriangle size={18} /> GPS non disponibile</>
          ) : geoStatus?.inside ? (
            <><CheckCircle2 size={18} /> {geoStatus.message}</>
          ) : (
            <><XCircle size={18} /> {geoStatus?.message}</>
          )}
        </div>

        {/* Status */}
        <div className={`tmb-status ${worker?.status === 'on_site' ? 'tmb-status-in' : 'tmb-status-out'}`}>
          {worker?.status === 'on_site' ? 'IN CANTIERE' : 'FUORI CANTIERE'}
        </div>

        {/* Last Action */}
        {lastAction && (
          <div className={`tmb-last ${lastAction.type === 'ingresso' ? 'tmb-last-in' : 'tmb-last-out'}`}>
            <CheckCircle2 size={20} />
            <span>{lastAction.type === 'ingresso' ? 'INGRESSO' : 'USCITA'} registrato alle {lastAction.time.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        )}

        {/* BIG BUTTONS */}
        <div className="tmb-buttons">
          <button className="tmb-btn tmb-btn-in" onClick={() => handleTimbratura('ingresso')} disabled={loading}>
            <LogIn size={40} />
            <span>INGRESSO</span>
          </button>
          <button className="tmb-btn tmb-btn-out" onClick={() => handleTimbratura('uscita')} disabled={loading}>
            <LogOut size={40} />
            <span>USCITA</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Timbra;
